import React from 'react';
import LegalPageLayout from '../layouts/LegalPageLayout';
import SEO from '../components/SEO';

const SECTIONS = [
    { id: 'intro',        label: 'Introduction' },
    { id: 'definitions',  label: 'Definitions & Key Terms' },
    { id: 'what',         label: 'What Is a Cookie?' },
    { id: 'why',          label: 'Why Do We Use Cookies?' },
    { id: 'types',        label: 'Types of Cookies We Use' },
    { id: 'manage',       label: 'How to Manage Cookies' },
    { id: 'blocking',     label: 'Blocking & Disabling Cookies' },
    { id: 'consent',      label: 'Your Consent' },
    { id: 'contact',      label: 'Contact Us' },
];

const h2 = { fontSize: '1.2rem', fontWeight: '800', color: '#1A1A2E', margin: '0 0 12px', fontFamily: 'Calibri, sans-serif' };
const divider = { borderTop: '2px solid rgba(173,20,87,0.1)', margin: '0 0 24px' };
const p = { color: '#475569', lineHeight: '1.8', marginBottom: '12px', fontFamily: 'Calibri, sans-serif' };
const li = { color: '#475569', lineHeight: '2', fontFamily: 'Calibri, sans-serif' };
const section = { marginBottom: '44px', scrollMarginTop: '90px' };

const CookiePolicy = () => (
    <>
        <SEO
            title="Cookie Policy"
            description="Learn about how Oottan uses cookies to improve your browsing experience and protect your privacy."
            canonical="/cookie-policy"
        />
        <LegalPageLayout
            title="Cookie Policy"
            updated="10 Oct 2026"
            sections={SECTIONS}
            currentPath="/cookie-policy"
        >
        <div id="intro" style={section}>
            <h2 style={h2}>Introduction</h2>
            <hr style={divider} />
            <p style={p}>
                This Cookie Policy explains how Oottan and its affiliates (collectively "Oottan," "We,"
                "us," and "ours") use cookies and similar technologies to recognise you when you visit our platform,
                including{' '}
                <a href="https://oottan.com" style={{ color: '#AD1457' }}>https://oottan.com</a>
                {' '}and any related URLs, mobile or localised versions, and related domains or sub-domains. It explains
                what these technologies are, why we use them, and the choices you have to control them.
            </p>
        </div>

        <div id="definitions" style={section}>
            <h2 style={h2}>Definitions and Key Terms</h2>
            <hr style={divider} />
            <ul style={{ paddingLeft: '20px' }}>
                <li style={li}><strong>Cookie:</strong> Small amount of data generated by a website and saved by your web browser. Used to identify your browser, provide analytics, and remember information such as your language preference or login information.</li>
                <li style={li}><strong>Company:</strong> Refers to <strong>Hirehike Technologies Private Limited, 8/61/1, Kumbavilai, Veeyanoor – 629177, Kanyakumari District, Tamil Nadu, India</strong>.</li>
                <li style={li}><strong>Device:</strong> Any internet-connected device such as a phone, tablet, or computer that can be used to visit Oottan and use the services.</li>
                <li style={li}><strong>Personal Data:</strong> Any information that directly or indirectly allows for the identification of a natural person.</li>
                <li style={li}><strong>Service:</strong> The service provided by Oottan as described on this platform.</li>
                <li style={li}><strong>Third-party Service:</strong> Advertisers, contest sponsors, promotional and marketing partners, and others who provide content or whose products we think may interest you.</li>
                <li style={li}><strong>Website:</strong> Oottan, accessible via <a href="https://oottan.com" style={{ color: '#AD1457' }}>https://oottan.com</a>.</li>
                <li style={li}><strong>You:</strong> A person or entity registered with Oottan to use the Services.</li>
            </ul>
        </div>

        <div id="what" style={section}>
            <h2 style={h2}>What Is a Cookie?</h2>
            <hr style={divider} />
            <p style={p}>
                A cookie is a small text file stored on your computer or other internet-connected device in order to
                identify your browser, provide analytics, and remember information about you such as your language
                preference or login information. They are completely safe and cannot be used to run programs or
                deliver viruses to your device.
            </p>
        </div>

        <div id="why" style={section}>
            <h2 style={h2}>Why Do We Use Cookies?</h2>
            <hr style={divider} />
            <p style={p}>We use first-party and/or third-party cookies on our platform for various purposes such as:</p>
            <ul style={{ paddingLeft: '20px' }}>
                <li style={li}>To facilitate the operation and functionality of our platform</li>
                <li style={li}>To improve your experience of our platform and make navigating quicker and easier</li>
                <li style={li}>To allow us to create a bespoke user experience and understand what is useful or of interest to you</li>
                <li style={li}>To analyse how our platform is used and how best we can customise it</li>
                <li style={li}>To identify future prospects and personalise marketing and sales interactions</li>
                <li style={li}>To facilitate the tailoring of online advertising to your interests</li>
            </ul>
        </div>

        <div id="types" style={section}>
            <h2 style={h2}>Types of Cookies We Use</h2>
            <hr style={divider} />
            <p style={p}>
                <strong>Essential Cookies —</strong> Strictly necessary to enable core functionality such as security,
                network management, and accessibility. Without them you would not be able to use basic services.
            </p>
            <p style={p}>
                <strong>Performance &amp; Functionality Cookies —</strong> Used to enhance the performance and
                functionality of our platform. Without them, certain features like videos may become unavailable or
                you may be required to log in every visit.
            </p>
            <p style={p}>
                <strong>Marketing Cookies —</strong> Enable us to identify future prospects and personalise sales and
                marketing interactions.
            </p>
            <p style={p}>
                <strong>Analytics &amp; Customisation Cookies —</strong> Collect information to help us understand how
                our platform is being used or how effective our marketing campaigns are.
            </p>
            <p style={p}>
                <strong>Advertising Cookies —</strong> Collect information over time about your online activity on
                the platform and other online services to make online advertisements more relevant and effective to you.
            </p>
            <p style={p}>
                <strong>Social Media Cookies —</strong> Used when you share information using a social media sharing
                button or "like" button on our site, or when you link your account or engage with our content on a
                social networking website.
            </p>
            <p style={p}>
                <strong>Third-Party Cookies —</strong> Some cookies on our platform are not set on a first-party
                basis. Third-party service providers may set their own cookies on your web browser. We do not control
                the use of these cookies.
            </p>
        </div>

        <div id="manage" style={section}>
            <h2 style={h2}>How You Can Manage Cookies</h2>
            <hr style={divider} />
            <p style={p}>
                Most browsers allow you to control cookies through their settings preferences. However, if you limit
                the ability of websites to set cookies, you may worsen your overall user experience since it will no
                longer be personalised to you. It may also stop you from saving customised settings like login
                information. Browser manufacturers provide help pages relating to cookie management in their products,
                including Google Chrome, Mozilla Firefox, Safari, and others.
            </p>
        </div>

        <div id="blocking" style={section}>
            <h2 style={h2}>Blocking and Disabling Cookies</h2>
            <hr style={divider} />
            <p style={p}>
                Wherever you are located, you may set your browser to block cookies and similar technologies. However,
                this action may block our essential cookies and prevent our website from functioning properly. You
                should also be aware that you may lose some saved information (e.g. saved login details, site
                preferences) if you block cookies. Different browsers make different controls available to you.
            </p>
        </div>

        <div id="consent" style={section}>
            <h2 style={h2}>Your Consent</h2>
            <hr style={divider} />
            <p style={p}>
                By using our platform, registering an account, or making a purchase, you hereby consent to our
                Cookie Policy and agree to its terms.
            </p>
        </div>

        <div id="contact" style={section}>
            <h2 style={h2}>Contact Us</h2>
            <hr style={divider} />
            <p style={p}>Don't hesitate to contact us if you have any questions about this Cookie Policy.</p>
            <ul style={{ paddingLeft: '20px' }}>
                <li style={li}>Email: <strong>info@oottan.com</strong></li>
                <li style={li}>Phone: <strong>04651-215020</strong></li>
                <li style={li}>Address: <strong>8/61/1, Kumbavilai, Veeyanoor – 629177, Kanyakumari District, Tamil Nadu, India</strong></li>
            </ul>
        </div>
        </LegalPageLayout>
    </>
);

export default CookiePolicy;
